import { Injectable } from "@angular/core";
import { ProvisioningService } from "./provisioning.service";
import { CbcNoHeaderReceiver } from "../crypto/cbc-NoHeader-Receiver";
import {
  importKey,
  encryptRawCbcZero,
  decryptRawCbcZero,
} from "../crypto/aes-cbc";

@Injectable({ providedIn: "root" })
export class BleSecureService {
  private _secure = false; // flips to true after ACK
  private _aesKey?: CryptoKey; // test key (zero)
  private rx2 = new CbcNoHeaderReceiver();
  private _IV: Uint8Array | undefined = undefined;

  constructor(private prov: ProvisioningService) {}

  get secure() {
    return this._secure;
  }

  /** TEST ONLY: enable secure mode (zero key + zero IV) after device ACKs */
  async initSecureTest(): Promise<void> {
    if (!globalThis.crypto?.subtle)
      throw new Error("SubtleCrypto not available");

    this._aesKey = await importKey(this.prov);
    this._secure = true;
  }

  disable(): void {
    this._secure = false;
    this._aesKey = undefined;
    this._IV = undefined;
    this.rx2.reset();
  }

  retrieveIV(IV: number[]) {
    this._IV = undefined;
    const ivs = this.expandIvVariants(IV);
    this._IV = ivs;
  }

  expandIvVariants(seed: number[]): Uint8Array {
    if (seed.length !== 6) {
      throw new Error(`Expected 6-byte IV seed, got ${seed.length}`);
    }

    const base = new Uint8Array(seed);

    const size = 16;

    var repeated = [];
    for (let i = 0; i < size; i++) {
      repeated.push(seed[i % seed.length]);
    }

    return new Uint8Array(repeated);
  }

  async encryptRawBeforeWrite(plaintext: Uint8Array): Promise<Uint8Array> {
    if (!this._secure || !this._aesKey || !this._IV) return plaintext; // still plaintext

    return encryptRawCbcZero(this._aesKey, plaintext, this._IV); // <-- RAW CT only
  }

  async decryptNotificationRaw(chunk: Uint8Array): Promise<Uint8Array[]> {
    if (!this._secure || !this._aesKey || !this._IV) return [chunk]; // plaintext phase

    const pt = await decryptRawCbcZero(this._aesKey, chunk, this._IV);

    return [pt];
  }
}
